import React, {
  useCallback,
  useEffect, useState
} from 'react';

import styled from 'styled-components';

import { TextField } from '@mui/material';
import {
  ButtonBlue, ButtonBlueLight, DateTimepicker, FlexContainer, InputCounter, Question,
  SelectOperator, SkeletonDetailIntervention, SmallWrapper, TitlePage
} from 'components';
import { useSnackbar } from 'notistack';
import { useForm, Controller, FieldErrors } from 'react-hook-form';
import { InterventionsService, OperatorsService } from 'services';
import {
  DATETIME_FORMAT,
  formatDateString,
  IndexedDBHelper,
  InterventionHelper, translate
} from 'utils/helpers';

import { useStores } from 'hooks';
import { observer } from 'mobx-react-lite';
import { CountryView, InterventionView, OperatorSummary } from 'models';
import { getDateFromAppointment } from 'models/detail/InterventionAppointment';
import { InterventionFrontView } from 'models/detail/InterventionFrontView';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { KEYCLOAK_STORE } from 'utils/helpers/IndexedDBHelper';
import { Equipement, EquipementOwner } from '../InterventionForm/Sections';

const StyledInterventionPlanification = styled.section`
  footer {
    margin-top: 4rem;
  }
`;

const COMMENT_LENGTH_MAX = 500;

interface FormStateProps {
  errors: FieldErrors<InterventionFrontView>;
  setField: (field: keyof InterventionFrontView | string, value: any) => void;
  values: InterventionFrontView;
}

interface TextareaProps {
  textarea: (name: string) => {
    name: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    value: string;
  };
}

interface EquipementOwnerProps {
  formState: FormStateProps;
  isRequired: boolean;
}

interface EquipementProps extends TextareaProps {
  formState: FormStateProps;
  isRequired: boolean;
}

const TypedEquipementOwner = EquipementOwner as React.FC<EquipementOwnerProps>;
const TypedEquipement = Equipement as React.FC<EquipementProps>;

export const InterventionPlanification = observer(() => {
  const { userStore } = useStores();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [availableOperators, setAvailableOperators] = useState<OperatorSummary[]>([]);

  const { id } = useParams();
  const idParam = id as string;
  const location = useLocation();
  const state = location.state as {country: CountryView, isNew: boolean};

  const defaultValues = new InterventionFrontView();
  const {
 control, handleSubmit, setValue, watch, formState: { errors }
} = useForm<InterventionFrontView>({
    defaultValues
  });

  const formValues = watch();

  const isId = (idValue: string) => idValue && idValue.includes('==');

  useEffect(() => {
    if (isId(idParam)) {
      setIsLoading(true);
      InterventionsService.getIntervention(idParam)
          .then((response) => {
            if (response) {
              const interventionMapped = InterventionHelper.BACKEND_MAPPER(response);
              Object.entries(interventionMapped).forEach(([key, value]) => {
                setValue(key as keyof InterventionFrontView, value);
              });
            }
          })
          .finally(() => setIsLoading(false));
    } else {
      setValue('fileNumber', idParam);
      setValue('country', state?.country);
    }
  }, [idParam, setValue, state]);

  useEffect(() => {
    OperatorsService.getOptions().then((response) => {
      if (response) {
        setAvailableOperators(response);
      }
    });
  }, []);

  const downloadInterventionsPending = useCallback((mobileId) => {
    if (mobileId && !userStore.isOffline) {
      return InterventionsService.getInterventionsPending(mobileId).then((interventions) => {
        if (interventions.length === 0) {
          return null;
        }

        const saveAllInterventions = Promise.all(interventions.map((intervention) => {
          const { hashId } = intervention;
          return InterventionHelper.saveInterventionDetailAndForm(hashId, intervention);
        }));

        return saveAllInterventions.then(() => {
          userStore.setRefreshInterventions(true);
          enqueueSnackbar(translate('common.interventionDowloaded', { nbInterventions: interventions.length }), { variant: 'success' });
        });
      })
          .catch(error => enqueueSnackbar(error?.message || error, { variant: 'error' }))
          .finally(() => {
            IndexedDBHelper.updateData({
              store: KEYCLOAK_STORE,
              key: `lastAutomaticInterventionDownload_${userStore.currentUser?.accountId}`,
              data: new Date()
            });
          });
    }

    return Promise.resolve();
  }, [userStore.currentUser, userStore.isOffline, enqueueSnackbar, userStore]);

  const goToInterventionDetail = (hashId) => {
    downloadInterventionsPending(userStore.deviceId).then(() => {
      enqueueSnackbar(translate('confirms.intervention.planified'), { variant: 'success' });
      navigate(`/interventions/${hashId}/detail`);
    });
  };

  const handleCancel = () => {
    if (isId(idParam)) {
      navigate(`/interventions/${idParam}/detail`);
    } else {
      navigate('/interventions');
    }
  };

  const onSubmit = (data) => {
    const newIntervention: InterventionView = {
      ...InterventionHelper.BUILD_PLANIFICATION(data),
      hashId: isId(idParam) ? idParam : ''
    };

    if (isId(idParam)) {
      InterventionsService.planIntervention(idParam, newIntervention).then(() => {
        goToInterventionDetail(idParam);
      });
    } else {
      InterventionsService.planIntervention(null, newIntervention).then((i) => {
        goToInterventionDetail(i.hashId);
      });
    }
  };

  const setFieldSafe = (field: string, value: any) => {
    setValue(field as any, value);
  };

  if (isLoading) {
    return <SkeletonDetailIntervention />;
  }

  return (
    <StyledInterventionPlanification>
      <form autoComplete="off" name="interventionPlanification" onSubmit={handleSubmit(onSubmit)}>
        <SmallWrapper>
          <TitlePage>
            {isId(idParam)
                  ? translate('pageInterventionPlanification.editIntervention')
                  : translate('pageInterventionPlanification.planIntervention')}
          </TitlePage>

          <Question label={`${translate('common.interventionNumber')} :`}>
            <Controller
                  control={control}
                  name="fileNumber"
                  render={({ field }) => (
                    <TextField
                          {...field}
                          error={!!errors.fileNumber}
                          inputProps={{ maxLength: 32 }}
                          label={translate('common.cerfaNumber')}
                          required
                      />
                  )}
                  rules={{ required: true }}
              />
          </Question>

          <Question label={`${translate('common.interventionDate')} :`}>
            <DateTimepicker
                  disablePast
                  error={!!errors.appointment}
                  label="common.interventionDate"
                  name="interventionDate"
                  required
                  value={getDateFromAppointment(formValues.appointment)}
                  onChange={value => {
                    if (value) {
                      const dateTime = formatDateString(value, DATETIME_FORMAT, DATETIME_FORMAT).split(' ');
                      setValue('appointment', {
                        date: dateTime[0],
                        time: dateTime[1]
                      });
                    } else {
                      setValue('appointment', null);
                    }
                  }}
              />
          </Question>

          <TypedEquipementOwner
                formState={{
                  values: formValues,
                  errors,
                  setField: setFieldSafe
                }}
                isRequired={!formValues.equipment}
            />

          <TypedEquipement
                formState={{
                  values: formValues,
                  errors,
                  setField: setFieldSafe
                }}
                isRequired={!formValues.equipmentOwner}
                textarea={(name) => ({
                  name,
                  onChange: (e) => setValue(name as any, e.target.value),
                  value: String(formValues[name as keyof InterventionFrontView] || '')
                })}
            />

          <Question label={`${translate('pageInterventionPlanification.assignedOperator')} :`}>
            <SelectOperator
                  isRequired
                  options={availableOperators}
                  value={formValues.operator}
                  onChange={value => setValue('operator', value)}
              />
          </Question>

          <Question label={`${translate('common.comment')} :`}>
            <InputCounter maxLength={COMMENT_LENGTH_MAX} value={formValues.comment || ''}>
              <Controller
                    control={control}
                    name="comment"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            inputProps={{
                              maxLength: COMMENT_LENGTH_MAX
                            }}
                            label={translate('pageInterventionPlanification.commentLabel')}
                            maxRows={6}
                            minRows={3}
                            multiline
                        />
                    )}
                />
            </InputCounter>
          </Question>

          <FlexContainer alignItems="center" as="footer" justifyContent="space-between">
            <ButtonBlueLight onClick={handleCancel}>
              {translate('button.cancel')}
            </ButtonBlueLight>
            <ButtonBlue data-cy="planificationSubmit" type="submit">
              {translate('button.planIntervention')}
            </ButtonBlue>
          </FlexContainer>
        </SmallWrapper>
      </form>
    </StyledInterventionPlanification>
  );
});
